import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  InputBase,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { INBOX_FILTERS, TV2_COLUMN_HEADER_HEIGHT } from "../../../constants/ticketV2Constants";
import { IconInbox24, IconPlus16 } from "./TicketV2Icons";
import { ReactComponent as Search } from "../../../assets/images/TicketImages/search.svg";

export const TICKETS_NAV_SIDEBAR_WIDTH = {
  xs: 224,
  xl: 260,
  xxl: 280,
};

const headerRowSx = {
  height: TV2_COLUMN_HEADER_HEIGHT,
  minHeight: TV2_COLUMN_HEADER_HEIGHT,
  boxSizing: "border-box",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: 2,
};

/** Section rows use `px: 1` (8px) + 24px icon → vertical guide sits at icon center. */
const SUB_LIST_RAIL_GUTTER_PX = 20;
/** 1px accent over rail: rail centered in gutter, list starts at gutter width → −(gutter − center). */
const ACTIVE_RAIL_LEFT_PX = -10;

function inboxCountForFilter(id, ticketCounts) {
  const tc = ticketCounts || {};
  if (id === "all") return tc.all ?? 0;
  if (id === "closed") return tc.closed ?? 0;
  if (id === "spam") return tc.spam ?? 0;
  if (id === "assigned_to_me") return tc.assigned_to_me ?? 0;
  if (id === "unassigned") return tc.unassigned ?? 0;
  return 0;
}

function TicketsSidebar({
  ticketCounts,
  activeInboxId = "all",
  onInboxSelect,
  onCreateTicket,
  searchQuery = "",
  onSearchChange,
}) {
  const [inboxOpen, setInboxOpen] = useState(true);

  return (
    <Box
      component="aside"
      sx={{
        width: "100%",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Box sx={headerRowSx}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#061522" }}>
          Tickets
        </Typography>
        <IconButton
          size="small"
          aria-label="Create ticket"
          onClick={() => onCreateTicket && onCreateTicket()}
          sx={{
            width: 24,
            height: 24,
            p: 0,
            borderRadius: "12px",
            border: "1px solid #E5E9EC",
            background: "#FFF",
            color: "#405261",
            "&:hover": {
              background: "#F9FAFA",
              borderColor: "#E5E9EC",
            },
          }}
        >
          <IconPlus16 />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 2,  py: 2 , pt:0, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            height: "32px",
            color: "#061522",
            borderRadius: "8px",
            border: "1px solid #D5DCE1",
            padding: "0 12px",
          }}
        >
          <Search />
          <InputBase
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder="Search"
            inputProps={{ "aria-label": "Search tickets" }}
            sx={{
              flex: 1,
              fontSize: 13,
              "&:focus": {
                borderColor: "#148DFD",
              },
              "& input::placeholder": { color: "#8B97A2", opacity: 1 },
            }}
          />
        </Box>

        <Box>
          <ListItemButton
            onClick={() => setInboxOpen((v) => !v)}
            sx={{
              borderRadius: "6px",
              px: 0,
              py: 1,
              color: "#405261",
              "&:hover": { bgcolor: "#F4F6F8" },
            }}
          >
            <Box sx={{ mr: 1.25, display: "flex", alignItems: "center", flexShrink: 0 }}>
              <IconInbox24 />
            </Box>
            <ListItemText
              primary="Inbox"
              primaryTypographyProps={{ fontSize: 16, color: "#405261" }}
            />
            {inboxOpen ? (
              <ExpandMoreIcon sx={{ fontSize: 18, color: "#8B97A2" }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 18, color: "#8B97A2" }} />
            )}
          </ListItemButton>
          <Collapse in={inboxOpen} unmountOnExit>
            <Stack direction="row" alignItems="stretch" sx={{ mt: 0.5 }} gap={0}>
              <Box
                aria-hidden
                sx={{
                  width: SUB_LIST_RAIL_GUTTER_PX,
                  flexShrink: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignSelf: "stretch",
                }}
              >
                <Box sx={{ width: "1px", alignSelf: "stretch", bgcolor: "#E5E9EC" }} />
              </Box>
              <List disablePadding sx={{ flex: 1, minWidth: 0, py: 0, overflow: "visible" }}>
                {INBOX_FILTERS.map((f) => {
                  const isActive = activeInboxId === f.id;
                  const count = inboxCountForFilter(f.id, ticketCounts);
                  return (
                    <ListItemButton
                      key={f.id}
                      selected={isActive}
                      onClick={() => onInboxSelect && onInboxSelect(f.id)}
                      sx={{
                        position: "relative",
                        borderRadius: "6px",
                        px: 1.25,
                        py: 0.75,
                        bgcolor: "transparent",
                        "&.Mui-selected": {
                          bgcolor: "transparent",
                        },
                        "&.Mui-selected:hover": {
                          bgcolor: "transparent",
                        },
                        "&:hover": { bgcolor: "transparent" },
                        ...(isActive && {
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: `${ACTIVE_RAIL_LEFT_PX}px`,
                            top: 0,
                            bottom: 0,
                            width: "1px",
                            bgcolor: "#148DFD",
                            zIndex: 1,
                            pointerEvents: "none",
                          },
                        }),
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                        <ListItemText
                          primary={f.label}
                          primaryTypographyProps={{
                            fontSize: 14,
                            fontWeight: isActive ? 500 : 400,
                            color: isActive ? "#061522" : "#405261",
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: isActive ? 500 : 400,
                            color: isActive ? "#061522" : "#405261",
                          }}
                        >
                          {count}
                        </Typography>
                      </Stack>
                    </ListItemButton>
                  );
                })}
              </List>
            </Stack>
          </Collapse>
        </Box>

        {/* Folders section hidden until folder feature is implemented
        <Box>
          <ListItemButton
            onClick={() => setFoldersOpen((v) => !v)}
            sx={{
              borderRadius: "6px",
              px: 0,
              py: 1,
              color: "#405261",
              "&:hover": { bgcolor: "#F4F6F8" },
            }}
          >
            <Box sx={{ mr: 1.25, display: "flex", alignItems: "center", flexShrink: 0 }}>
              <IconFolder24 />
            </Box>
            <ListItemText
              primary="Folders"
              primaryTypographyProps={{ fontSize: 16, color: "#405261" }}
            />
            {foldersOpen ? (
              <ExpandMoreIcon sx={{ fontSize: 18, color: "#8B97A2" }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 18, color: "#8B97A2" }} />
            )}
          </ListItemButton>
          <Collapse in={foldersOpen} unmountOnExit>
            <Stack direction="row" alignItems="stretch" sx={{ mt: 0.5 }} gap={0}>
              <Box
                aria-hidden
                sx={{
                  width: SUB_LIST_RAIL_GUTTER_PX,
                  flexShrink: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignSelf: "stretch",
                }}
              >
                <Box sx={{ width: "1px", alignSelf: "stretch", bgcolor: "#E5E9EC" }} />
              </Box>
              <List disablePadding sx={{ flex: 1, minWidth: 0, py: 0 }}>
                {FOLDERS.map((f) => (
                  <ListItemButton
                    key={f.id}
                    sx={{
                      borderRadius: "6px",
                      px: 1.25,
                      py: 0.75,
                      "&:hover": { bgcolor: "#F4F6F8" },
                    }}
                  >
                    <ListItemText
                      primary={f.label}
                      primaryTypographyProps={{ fontSize: 14, color: "#405261" }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Stack>
          </Collapse>
        </Box>
        <Box>
        <ListItemButton
          sx={{
            borderRadius: "6px",
            px: 0,

            py: 1,
            color: "#148DFD",
            "&:hover": { bgcolor: "#F4F6F8" },
          }}
        >
          <Box
            sx={{
              mr: 1.25,
              width: 32,
              height: 32,
              flexShrink: 0,
              borderRadius: "50%",
              border: "1px solid #CCE4FF",
              bgcolor: "#FFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AddIcon sx={{ fontSize: 18, color: "#148DFD" }} />
          </Box>
          <ListItemText
            primary="Create Folder"
            primaryTypographyProps={{ fontSize: 14, fontWeight: 400, color: "#148DFD" }}
            />
          </ListItemButton>
        </Box>
        */}
      </Box>
    </Box>
  );
}

export default TicketsSidebar;
